import { useEffect, useState } from "react";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Button,
    Input,
    FormControl,
    FormLabel,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
} from "@chakra-ui/react";

const EDIT_TYPE_TITLES = {
    category: "kategoriju",
    property: "svojstvo",
    entry: "unos",
};

// The GET payload returns full ISO datetimes for entries, but the date
// <Input> and the POST/PUT body both want a bare yyyy-MM-dd.
const normalizeInitialData = (data) => {
    if (!data?.expirationDate) return data || {};
    return { ...data, expirationDate: data.expirationDate.slice(0, 10) };
};

const EditModal = ({ isOpen, onClose, onSave, editData: initialData, editType }) => {
    const [formData, setFormData] = useState(normalizeInitialData(initialData));
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setFormData(normalizeInitialData(initialData));
        }
    }, [isOpen, initialData]);

    if (!formData) return null;

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await onSave(formData);
        } finally {
            setIsSaving(false);
        }
    };

    const isAddingProperty = editType === "property" && !formData?.id;
    const isEditingProperty = editType === "property" && !!formData?.id;
    const isAddingEntry = editType === "entry" && !formData?.id;

    let headerTitle;
    if (isAddingProperty) {
        headerTitle = `${formData?.categoryName || ""} - Unos`;
    } else if (isEditingProperty) {
        headerTitle = `Uredi ${formData?.name || ""}`;
    } else if (isAddingEntry) {
        headerTitle = `${formData?.propertyName || ""} - Dodaj unos`;
    } else {
        headerTitle = `${formData?.id ? "Uredi" : "Dodaj"} ${EDIT_TYPE_TITLES[editType] || editType}`;
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{headerTitle}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    {editType === "category" && (
                        <>
                            <FormControl mb={3}>
                                <FormLabel>Naziv</FormLabel>
                                <Input
                                    placeholder="npr. Zaposlenici"
                                    value={formData?.name || ""}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Broj dana prije isteka za obavijest</FormLabel>
                                <NumberInput
                                    min={0}
                                    value={formData?.notifyDaysBefore ?? 0}
                                    onChange={(_, valueAsNumber) =>
                                        setFormData({
                                            ...formData,
                                            notifyDaysBefore: Number.isNaN(valueAsNumber) ? 0 : valueAsNumber,
                                        })
                                    }
                                >
                                    <NumberInputField />
                                    <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                    </NumberInputStepper>
                                </NumberInput>
                            </FormControl>
                        </>
                    )}

                    {editType === "property" && (
                        <FormControl>
                            <FormLabel>Naziv</FormLabel>
                            <Input
                                placeholder="npr. Primjer unosa"
                                value={formData?.name || ""}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </FormControl>
                    )}

                    {editType === "entry" && (
                        <>
                            <FormControl mb={3}>
                                <FormLabel>Subjekt</FormLabel>
                                <Input
                                    placeholder="npr. Primjer unosa"
                                    value={formData?.subjectName || ""}
                                    onChange={(e) => setFormData({ ...formData, subjectName: e.target.value })}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Datum isteka</FormLabel>
                                <Input
                                    type="date"
                                    value={formData?.expirationDate || ""}
                                    onChange={(e) => setFormData({ ...formData, expirationDate: e.target.value })}
                                />
                            </FormControl>
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        bg="brand.600"
                        color="white"
                        _hover={{ bg: "brand.700" }}
                        mr={3}
                        onClick={handleSave}
                        isLoading={isSaving}
                        loadingText="Spremanje..."
                        isDisabled={isSaving}
                    >
                        Spremi
                    </Button>
                    <Button onClick={onClose} isDisabled={isSaving}>Povratak</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default EditModal;
